import {
  CarePlanManifestationResponse,
  CarePlanProblemResponse,
} from '~/lib/types/carePlan/carePlans';
import {
  ActionBar,
  ChakraBox,
  ChakraFlex,
  ChakraHStack,
  FAIconName,
  FireflyButton,
  ModalV2,
  MultiSelectOption,
  SkeletonLoader,
  Text,
  TextInputV2,
  UniversalSelect,
  useToastMessage,
} from '@equip.health/ui';
import { useEffect, useMemo, useState } from 'react';
import { useUpdateProblemMutation } from '~/store/services/carePlan';
import { AddEditManifestationRow } from '~/components/maud/patients/profile/tabs/careplan/AddEditManifestationRow';
import {
  useGetRefCarePlanManifestationsEnumQuery,
  useGetRefCarePlanProblemsEnumQuery,
} from '~/store/services/refCarePlanEnums';
import Loading from '~/pages/loading';
import { isEqual } from 'lodash';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { ManifestationCodeToEditableDataMap } from '~/lib/types/carePlan/ManifestationCodeToEditableDataMap';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCarePlanFrequencyOptions } from '~/lib/hooks/useCarePlanFrequencyOptions';

type Props = {
  treatmentEpisodeId: string;
  problem: CarePlanProblemResponse;
  onClose: () => void;
};
const TOP_LEVEL_ID = 'edit-problem-modal';

const manifestationsToEditableDataMap = (
  manifestations: CarePlanManifestationResponse[],
) => {
  const manifestationCodeToDescriptionMap: ManifestationCodeToEditableDataMap =
    {};
  manifestations.forEach(
    (manifestation) =>
      (manifestationCodeToDescriptionMap[
        manifestation.pathwayManifestation.code
      ] = {
        description: manifestation.description,
        statusCode: manifestation.statusCode,
        frequencyCode: manifestation.frequency?.code || undefined,
      }),
  );
  return manifestationCodeToDescriptionMap;
};

export const EditProblemModal = ({
  problem,
  treatmentEpisodeId,
  onClose,
}: Props) => {
  const { trackEvent } = useAnalytics();
  const {
    carePlanProviderFacingLanguage: isProviderFacingLanguageEnabled,
    manifestationProblemStatusFrequency: isFrequencyDisplayEnabled,
  } = useFlags<LDFlags>();

  const { data: refProblems, isLoading: isGetRefProblemsLoading } =
    useGetRefCarePlanProblemsEnumQuery({});

  const { data: manifestations, isLoading: isGetManifestationsLoading } =
    useGetRefCarePlanManifestationsEnumQuery({
      problemCodes: [problem.pathwayProblem.code],
    });

  const refProblem = useMemo(
    () => refProblems?.find((p) => p.code === problem.pathwayProblem.code),
    [problem, refProblems],
  );

  const [
    updateProblem,
    {
      isLoading: isUpdateProblemLoading,
      isSuccess: isUpdateProblemSuccess,
      isError: isUpdateProblemError,
    },
  ] = useUpdateProblemMutation();

  const { createToast } = useToastMessage();

  const [problemDescription, setProblemDescription] = useState(
    problem.description,
  );

  const [problemFrequencyCode, setProblemFrequencyCode] = useState<
    string | null
  >(problem.frequency?.code || null);

  const {
    options: frequencyOptions,
    placeholder: frequencyPlaceholder,
    isLoading: isGetFrequencyOptionsLoading,
  } = useCarePlanFrequencyOptions({
    frequencyUnitType: refProblem?.frequencyUnitType,
    frequencyCode: problemFrequencyCode || undefined,
  });

  const [selectedManifestationsMap, setSelectedManifestationsMap] =
    useState<ManifestationCodeToEditableDataMap>(() => {
      return manifestationsToEditableDataMap(problem.manifestations);
    });

  useEffect(() => {
    if (isUpdateProblemError) {
      createToast({
        icon: FAIconName.exclamationTriangleRegular,
        id: `${TOP_LEVEL_ID}_error-toast`,
        text: "We're sorry, we were unable to update this problem. Please try again later",
        variant: 'critical',
      });
      onClose();
    }
  }, [isUpdateProblemError]);

  useEffect(() => {
    if (isUpdateProblemSuccess) {
      onClose();
    }
  }, [isUpdateProblemSuccess]);

  const isChanged =
    problem.description !== problemDescription ||
    (problem.frequency?.code || null) !== problemFrequencyCode ||
    !isEqual(
      manifestationsToEditableDataMap(problem.manifestations),
      selectedManifestationsMap,
    );

  if (isGetManifestationsLoading) {
    return <Loading />;
  }

  const noManifestationsSelected =
    Object.keys(selectedManifestationsMap).length === 0;

  return (
    <ModalV2
      footerContent={
        <ChakraHStack
          justifyContent="space-between"
          paddingTop="14px"
          width="100%"
        >
          <FireflyButton
            id={`${TOP_LEVEL_ID}-cancel`}
            isLoading={isUpdateProblemLoading}
            leftIcon={FAIconName.timesRegular}
            onClick={() => {
              trackEvent(CarePlanEvents.clickOnCancelButtonInEditProblemModal);
              onClose();
            }}
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}-save`}
            isDisabled={
              !isChanged ||
              isGetFrequencyOptionsLoading ||
              isGetRefProblemsLoading ||
              (manifestations.length > 0 && noManifestationsSelected)
            }
            isLoading={isUpdateProblemLoading}
            leftIcon={FAIconName.checkRegular}
            onClick={() => {
              trackEvent(
                CarePlanEvents.clickOnSaveChangesButtonInEditProblemModal,
              );
              updateProblem({
                treatmentEpisodeId,
                problemId: problem.id,
                body: {
                  description: problemDescription,
                  frequencyCode: problemFrequencyCode,
                  manifestations: Object.entries(selectedManifestationsMap).map(
                    ([
                      pathwayManifestationCode,
                      { description, frequencyCode, statusCode },
                    ]) => ({
                      pathwayManifestationCode,
                      description,
                      statusCode,
                      frequencyCode,
                    }),
                  ),
                },
              });
            }}
            variant="primary"
          >
            Save changes
          </FireflyButton>
        </ChakraHStack>
      }
      id={TOP_LEVEL_ID}
      isOpen
      onClose={() => {
        trackEvent(CarePlanEvents.clickOnCloseIconInEditProblemModal);
        onClose();
      }}
      size="4xl"
      title="Edit problem"
    >
      <Text>
        Please only remove manifestations here if they were added in error. To
        document changes to the patient's progress over the course of treatment,
        please update the statuses on goals and interventions.
      </Text>

      <ChakraBox
        border="1px"
        borderColor="neutral.line"
        borderRadius="12px"
        marginTop="24px"
        padding="18px"
      >
        <Text marginBottom="18px" variant="h5">
          {problem.pathwayProblem.label}
        </Text>
        {manifestations.length === 0 && (
          <>
            <TextInputV2
              id="edit-problem-description"
              onChange={(newDescription) => {
                setProblemDescription(newDescription);
              }}
              placeholder="Please add any additional context"
              value={problemDescription}
            />
            {(isProviderFacingLanguageEnabled || isFrequencyDisplayEnabled) && (
              <ChakraFlex alignItems="center" gap="12px" marginTop="12px">
                {isProviderFacingLanguageEnabled &&
                  problem.pathwayProblem.providerFacingLanguage && (
                    <Text>{problem.pathwayProblem.providerFacingLanguage}</Text>
                  )}
                {isFrequencyDisplayEnabled && refProblem?.frequencyUnitType && (
                  <SkeletonLoader
                    id="edit-problem-select-frequency-loader"
                    isLoaded={!isGetFrequencyOptionsLoading}
                  >
                    <ChakraBox width="200px">
                      <UniversalSelect
                        ariaLabel="Select frequency"
                        id="edit-problem-select-frequency"
                        isClearable={false}
                        isSingleSelect
                        menuPlacement="auto"
                        modalId={TOP_LEVEL_ID}
                        onChange={(newFrequency: MultiSelectOption) => {
                          setProblemFrequencyCode(newFrequency.value);
                        }}
                        options={frequencyOptions}
                        placeholder={frequencyPlaceholder}
                        value={frequencyOptions.find(
                          (option) => option.value === problemFrequencyCode,
                        )}
                        variant="small"
                      />
                    </ChakraBox>
                  </SkeletonLoader>
                )}
              </ChakraFlex>
            )}
          </>
        )}
        {manifestations.length > 0 && (
          <ChakraFlex flexDirection="column" gap="24px">
            {manifestations.map((manifestation) => (
              <AddEditManifestationRow
                isForEditProblem
                key={manifestation.code}
                manifestation={manifestation}
                modalId={TOP_LEVEL_ID}
                selectedManifestationsMap={selectedManifestationsMap}
                setSelectedManifestationsMap={setSelectedManifestationsMap}
              />
            ))}
          </ChakraFlex>
        )}
      </ChakraBox>

      {manifestations.length > 0 && noManifestationsSelected && (
        <ChakraBox marginY="24px">
          <ActionBar
            barIcon={FAIconName.exclamationTriangleRegular}
            id="edit-problem-no-manifestations"
            text="This problem requires at least one manifestation. To remove the entire problem, please return to the care plan and remove the associated goal instead."
            variant="warning"
          />
        </ChakraBox>
      )}
    </ModalV2>
  );
};
